/**
 * Default header and bubble for FloatingPanel. Callers with their own chrome
 * (the legacy map window) pass renderHeader / renderBubble instead.
 */
import React, { forwardRef } from "react";

import { joinClassNames } from "./frameStyle";
import { PANEL_STATES } from "./floatingPanelConstants";

const ICON_PROPS = {
  width: 14, height: 14, fill: "none", stroke: "currentColor", strokeWidth: 2,
  viewBox: "0 0 24 24", "aria-hidden": true, focusable: "false",
};
const MAX_BADGE = 99;

export function FloatingPanelHeader({
  title, titleId, panelState, isNarrow, dragHandleProps, minimise, maximise, close, headerActions = null,
}) {
  const isMaximised = panelState === PANEL_STATES.MAXIMISED;
  return (
    <div className="d5-brain-header" {...(dragHandleProps || {})}>
      <h2 id={titleId} className="d5-brain-title">{title}</h2>
      <div className="d5-brain-header-actions">
        {/* Caller controls (e.g. the popover's Exit chip) sit left of the window icons, split by a hairline. */}
        {headerActions && (
          <>
            {headerActions}
            <span className="d5-brain-header-divider" aria-hidden="true" />
          </>
        )}
        <button type="button" className="d5-brain-icon-btn" aria-label="Minimise" title="Minimise" onClick={minimise}>
          <svg {...ICON_PROPS}><path d="M5 12h14" /></svg>
        </button>
        {/* One element for both labels so focus restore after maximise lands back on it. */}
        {!isNarrow && (
          <button
            type="button"
            className="d5-brain-icon-btn"
            aria-label={isMaximised ? "Restore" : "Maximise"}
            title={isMaximised ? "Restore" : "Maximise"}
            onClick={isMaximised ? close : maximise}
          >
            {isMaximised
              ? <svg {...ICON_PROPS}><rect x="7" y="7" width="12" height="12" rx="1" /><path d="M5 15V5h10" /></svg>
              : <svg {...ICON_PROPS}><rect x="5" y="5" width="14" height="14" rx="1" /></svg>}
          </button>
        )}
        <button type="button" className="d5-brain-icon-btn d5-brain-icon-btn--close" aria-label="Close" title="Close" onClick={close}>
          <svg {...ICON_PROPS}><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>
    </div>
  );
}

const updatesText = (count) => `${count} update${count === 1 ? "" : "s"}`;

export const FloatingPanelBubble = forwardRef(function FloatingPanelBubble(
  { name, count = 0, pulse = false, className, zIndex, top, onOpen }, ref,
) {
  const hasUpdates = count > 0;
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={joinClassNames("d5-brain-bubble", pulse && hasUpdates && "d5-brain-bubble--pulse", className)}
        style={top == null ? { zIndex } : { zIndex, top }}
        aria-label={`Open ${name} (${updatesText(count)})`}
        onClick={onOpen}
      >
        <svg {...ICON_PROPS} width={18} height={18}>
          <circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" />
          <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
        </svg>
        {hasUpdates && (
          <span className="d5-brain-bubble-badge" aria-hidden="true">
            {count > MAX_BADGE ? `${MAX_BADGE}+` : count}
          </span>
        )}
      </button>
      <span className="d5-brain-sr-only" aria-live="polite">
        {hasUpdates ? `${name}: ${updatesText(count)}` : ""}
      </span>
    </>
  );
});
